import { useState } from 'react';
import { useUnit } from 'effector-react';
import { FormProvider, useForm } from 'react-hook-form';
import { $accountByIdStore, $profileFeatureStore } from '@lib/store';
import type { AccountByIdResponse } from '@shared/api/rest/accounts';
import { ModalAccountDetails } from '@pages';
import { Item } from './item';
import { Button, Icon } from '@shared/widgets';
import ShareIcon from '@assets/icons/share2.svg';

export interface IForm {
  accountLabel: string;
  costCentre: string;
}

interface Requisite {
  name: React.ReactNode;
  value?: React.ReactNode;
}

interface IRequisitesProps {
  values: Requisite[];
  data: AccountByIdResponse | null;
}

export const Requisites = (props: IRequisitesProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const methods = useForm<IForm>({
    mode: 'onSubmit',
    defaultValues: { accountLabel: props.data?.accountLabel || '', costCentre: props.data?.costCentre || '' },
  });
  const accountLabel = methods.watch('accountLabel');
  const costCentre = methods.watch('costCentre');

  const submitHandler = ({ accountLabel, costCentre }: IForm) => {
    if (!props.data?.id) return;

    $accountByIdStore.events.updateDetails({
      request: { accountId: props.data.id, label: accountLabel, costCentre, category: props.data.category! },
      uId: props.data.id,
    });
  };

  const canEditLabel = !!profileFeatureStore.data?.accountFlows?.[0]?.editAccountLabel;
  const canEditCost = !!profileFeatureStore.data?.accountFlows?.[0]?.editCostCentre;
  return (
    <div className='flex flex-col gap-1'>
      <FormProvider {...methods}>
        <form className='flex flex-col gap-1' onSubmit={methods.handleSubmit(submitHandler)}>
          {props.values?.map((item, i) => (
            <Item
              key={i}
              name={item.name as string}
              value={item.value as string}
              isEditableLabel={canEditLabel && i === 0}
              isEditableCost={canEditCost && i === 1}
              isCopy={(i === 0 && !canEditLabel) || (i === 1 && !canEditCost) || i >= 2}
            />
          ))}
          <Button color='outlined' fullWidth className='mt-4' onClick={() => setIsModalOpen(true)}>
            <Icon icon={<ShareIcon />} color='inherit' />
            Share account details
          </Button>
          {(props.data?.accountLabel !== accountLabel || props.data?.costCentre !== costCentre) && (
            <Button type='submit' color='primary' fullWidth className='mt-4'>
              Save changes
            </Button>
          )}
        </form>
      </FormProvider>
      <ModalAccountDetails
        values={props.values ? props.values.map((item) => ({ name: item.name, value: item.value })) : []}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
